import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg, t } from '@lingui/core/macro'
import { format } from 'date-fns'
import { activeDateFnsLocale } from 'features/locale/dateFnsLocale'

import type {
  MdCase,
  MdCasePromotionState,
  MdCaseSeverity,
  MdCaseSourceType,
  MdCaseStatus,
  MdCaseWorkflowMode,
} from './mdCase'

export type MdCasesPublishedStateDisplay = 'published' | 'unpublished'

const MD_CASE_STATUS_LABELS: Record<MdCaseStatus, MessageDescriptor> = {
  'New Alert': msg`New Alert`,
  Open: msg({ message: 'Open', context: 'case status' }),
  Resolved: msg`Resolved`,
  Closed: msg`Closed`,
  Cleared: msg`Cleared`,
  Shelved: msg`Shelved`,
}

const MD_CASE_SEVERITY_LABELS: Record<MdCaseSeverity, MessageDescriptor> = {
  High: msg`High`,
  Medium: msg`Medium`,
  Low: msg`Low`,
}

const MD_CASE_WORKFLOW_MODE_LABELS: Record<
  MdCaseWorkflowMode,
  MessageDescriptor
> = {
  watch: msg`Watch`,
  investigate: msg`Investigate`,
}

const MD_CASE_SOURCE_TYPE_LABELS: Record<MdCaseSourceType, MessageDescriptor> =
  {
    manual: msg`Manual`,
    alert: msg`Alert`,
  }

const MD_CASE_PROMOTION_STATE_LABELS: Record<
  MdCasePromotionState,
  MessageDescriptor
> = {
  sent_to_site: msg`Sent To Site`,
  roc_only: msg`ROC Only`,
}

/** Translated label for a stored M&D case status; the stored value stays English. */
export const getMdCaseStatusLabel = (status: MdCaseStatus | null): string =>
  status === null ? t`Unknown` : i18n._(MD_CASE_STATUS_LABELS[status])

/** Translated label for a stored M&D severity; the stored value stays English. */
export const getMdCaseSeverityLabel = (
  severity: MdCaseSeverity | null
): string =>
  severity === null ? t`Unknown` : i18n._(MD_CASE_SEVERITY_LABELS[severity])

export const getMdCaseWorkflowModeLabel = (
  workflowMode: MdCaseWorkflowMode | null
): string =>
  workflowMode === null
    ? t`Unknown`
    : i18n._(MD_CASE_WORKFLOW_MODE_LABELS[workflowMode])

export const getMdCaseSourceTypeLabel = (
  sourceType: MdCaseSourceType | null
): string =>
  sourceType === null
    ? t`Unknown`
    : i18n._(MD_CASE_SOURCE_TYPE_LABELS[sourceType])

export const getMdCasePromotionStateLabel = (
  promotionState: MdCasePromotionState | null
): string =>
  promotionState === null
    ? t`Unknown`
    : i18n._(MD_CASE_PROMOTION_STATE_LABELS[promotionState])

export const getMdCasesPublishedStateLabel = (
  publishedState: MdCasesPublishedStateDisplay
): string => (publishedState === 'published' ? t`Published` : t`Unpublished`)

// ── Severity tokens ──

export const SEVERITY_TOKENS = {
  High: { tone: 'danger' as const, color: '#f25555', rank: 0 },
  Medium: { tone: 'warning' as const, color: '#d48f31', rank: 1 },
  Low: { tone: 'accent' as const, color: '#3cab67', rank: 2 },
} as const

export type MdCaseTone =
  | 'default'
  | 'danger'
  | 'warning'
  | 'success'
  | 'accent'
  | 'muted'

const PUBLISHED_STATE_TOKENS = {
  published: { tone: 'success' as const, rank: 0 },
  unpublished: { tone: 'muted' as const, rank: 1 },
} as const

export const getPublishedStateTone = (
  publishedState: MdCasesPublishedStateDisplay
): MdCaseTone => PUBLISHED_STATE_TOKENS[publishedState].tone

export const getPublishedStateRank = (
  publishedState: MdCasesPublishedStateDisplay
): number => PUBLISHED_STATE_TOKENS[publishedState].rank

export const getSeverityTone = (severity: MdCaseSeverity | null): MdCaseTone =>
  severity === null ? 'default' : SEVERITY_TOKENS[severity].tone

export const getSeverityColor = (severity: MdCaseSeverity | null): string =>
  severity === null ? '#888' : SEVERITY_TOKENS[severity].color

export const getSeverityRank = (severity: MdCaseSeverity | null): number =>
  severity === null ? Number.MAX_SAFE_INTEGER : SEVERITY_TOKENS[severity].rank

// ── Status tokens ──

export const STATUS_TOKENS = {
  'New Alert': { tone: 'warning' as const, rank: 0, isArchived: false },
  Open: { tone: 'success' as const, rank: 1, isArchived: false },
  Resolved: { tone: 'accent' as const, rank: 2, isArchived: false },
  Closed: { tone: 'muted' as const, rank: 3, isArchived: true },
  Cleared: { tone: 'muted' as const, rank: 4, isArchived: true },
  Shelved: { tone: 'default' as const, rank: 5, isArchived: false },
} as const

export const getStatusTone = (status: MdCaseStatus | null): MdCaseTone =>
  status === null ? 'default' : STATUS_TOKENS[status].tone

export const getStatusRank = (status: MdCaseStatus | null): number =>
  status === null ? Number.MAX_SAFE_INTEGER : STATUS_TOKENS[status].rank

// ── Date formatting ──

export const formatMdCaseDisplayDateTime = (
  value: string | null | undefined
): string => {
  if (!value) return t`Unknown`
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : format(date, 'MMM d, yyyy · h:mm a', { locale: activeDateFnsLocale() })
}

export const formatMdCaseDisplayCompactDate = (
  value: string | null | undefined
): string => {
  if (!value) return t`Unknown`
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  const now = new Date()
  return format(
    date,
    date.getFullYear() === now.getFullYear() ? 'MMM d' : 'MMM d, yyyy',
    { locale: activeDateFnsLocale() }
  )
}

export const formatMdCaseDateTimeRange = (from: string, to: string): string => {
  const fromLabel = formatMdCaseDisplayDateTime(from)
  const toLabel = formatMdCaseDisplayDateTime(to)
  return fromLabel === toLabel
    ? t`at ${fromLabel}`
    : t`${fromLabel} -> ${toLabel}`
}

// ── Publish state label ──

export const getMdCasePublishStateLabel = (mdCase: MdCase): string => {
  if (
    mdCase.workflow.published_at ||
    mdCase.workflow.promotion_state === 'sent_to_site'
  ) {
    return t`Published`
  }

  if (mdCase.workflow.promotion_state === 'roc_only') {
    return t`ROC Only`
  }

  return t`Draft`
}
